<template>
  <div class="relative w-full overflow-x-auto">
    <!-- Loading overlay -->
    <div v-if="loading" class="absolute inset-0 bg-base-100 bg-opacity-70 flex items-center justify-center z-10">
      <span class="loading loading-spinner loading-lg text-primary"></span>
    </div>

    <div class="overflow-x-auto">
      <table class="table" :class="{ 'table-zebra': striped }">
        <thead>
          <tr>
            <th v-if="detailed" class="w-10"></th>
            <slot name="header">
              <th
                v-for="column in columns"
                :key="column.field"
                :class="{ 'cursor-pointer': column.sortable }"
                @click="column.sortable && onHeaderClick(column.field)"
              >
                {{ column.label }}

                <d-icon 
                  v-if="column.sortable && sortField === column.field" 
                  size="sm"
                  :icon="sortOrder === 'asc' ? 'arrow-up' : 'arrow-down'"
                  class="ml-2" 
                />
              </th>
            </slot>
          </tr>
        </thead>
        <tbody>
          <template v-for="(row, index) in data" :key="row[detailKey] || index">
            <tr
              :class="{
                'bg-primary/10': isRowSelected(row),
                'hover': !isRowSelected(row),
              }"
              @click="onRowClick(row, index)"
            >
              <td
                v-if="detailed"
                class="w-10 text-center"
                @click.stop="toggleDetails(row)"
              >
                <button class="btn btn-circle btn-xs text-lg bg-transparent">
                  <d-icon :icon="isDetailShown(row) ? 'chevron-down' : 'chevron-right'" size="sm" />
                </button>
              </td>
              <slot name="default" :row="row" :index="index">
                <td v-for="column in columns" :key="column.field">
                  {{ row[column.field] }}
                </td>
              </slot>
            </tr>
            <tr v-if="detailed && isDetailShown(row)">
              <td :colspan="columns.length + (detailed ? 1 : 0)" class="bg-base-200">
                <div class="p-4">
                  <slot name="detail" :row="row" :index="index"></slot>
                </div>
              </td>
            </tr>
          </template>
          <tr v-if="data.length === 0">
            <td
              :colspan="columns.length + (detailed ? 1 : 0)"
              class="text-center"
            >
              <slot name="empty"> No data available </slot>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
export default {
  name: "DTable",

  props: {
    data: {
      type: Array,
      default: () => [],
    },
    columns: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    striped: {
      type: Boolean,
      default: false,
    },
    detailed: {
      type: Boolean,
      default: false,
    },
    detailKey: {
      type: String,
      default: "id",
    },
    defaultSort: {
      type: Array,
      default: () => [],
    },
    defaultSortDirection: {
      type: String,
      default: "asc",
      validator: (value) => ["asc", "desc"].includes(value),
    },
    backendSearch: {
      type: Boolean,
      default: false,
    },
    backendSorting: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      sortField: this.defaultSort[0] || "",
      sortOrder: this.defaultSort[1] || this.defaultSortDirection,
      selectedRow: null,
      visibleDetailRows: [],
    };
  },

  methods: {
    onHeaderClick(field) {
      if (this.sortField === field) {
        this.sortOrder = this.sortOrder === "asc" ? "desc" : "asc";
      } else {
        this.sortField = field;
        this.sortOrder = this.defaultSortDirection;
      }

      this.$emit("sort", this.sortField, this.sortOrder);
    },

    onRowClick(row, index) {
      this.selectedRow = row;
      this.$emit("row-click", row, index);
    },

    isRowSelected(row) {
      return this.selectedRow === row;
    },

    toggleDetails(row) {
      const key = row[this.detailKey];
      const index = this.visibleDetailRows.findIndex((r) => r === key);

      if (index >= 0) {
        this.visibleDetailRows.splice(index, 1);
      } else {
        this.visibleDetailRows.push(key);
      }
    },

    isDetailShown(row) {
      return this.visibleDetailRows.includes(row[this.detailKey]);
    },
  },
};
</script>

<style scoped>
tr:not(.is-selected):nth-child(2n) {
  background-color: #eaeaea;
}
</style>
